import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  ReactNode,
  useMemo,
} from 'react';
import {Box, useMediaQuery, useTheme} from '@mui/material';
import './ArrSplitPanel.scss';

interface ArrSplitPanelInterface {
  leftPanel: ReactNode;
  rightPanel: ReactNode;
  fixedRightPanelWidth?: boolean;
  maxLeftPanelWidth?: number;
  maxLeftPanelRatio?: number;
}
const ArrSplitPanel: React.FC<ArrSplitPanelInterface> = ({
  leftPanel,
  rightPanel,
  fixedRightPanelWidth = false,
  maxLeftPanelWidth,
  maxLeftPanelRatio,
}) => {
  const MIN_SIZE = useMemo(() => maxLeftPanelWidth ?? 280, [maxLeftPanelWidth]);
  const [leftWidth, setLeftWidth] = useState<number>(MIN_SIZE);
  const [leftHeight, setLeftHeight] = useState<number>(
    () => window.innerHeight / 2
  );
  useEffect(() => {
    setLeftWidth(MIN_SIZE);
  }, [MIN_SIZE]);

  const containerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef<boolean>(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(
    `(max-width: ${theme.breakpoints.values.md}px)`
  );

  const handleMouseDown = useCallback(() => {
    isDragging.current = true;
    document.body.style.cursor = isMobile ? 'row-resize' : 'col-resize';
  }, [isMobile]);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging.current || !containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const minSize = MIN_SIZE;

      if (isMobile) {
        const newHeight = e.clientY - containerRect.top;
        const maxHeight = containerRect.height - minSize;
        if (newHeight > minSize && newHeight < maxHeight) {
          setLeftHeight(newHeight);
        }
      } else {
        const newWidth = e.clientX - containerRect.left;
        const maxWidth =
          maxLeftPanelRatio != null
            ? Math.max(minSize, containerRect.width * maxLeftPanelRatio)
            : containerRect.width - minSize;
        const clampedWidth = Math.min(Math.max(newWidth, minSize), maxWidth);
        setLeftWidth(clampedWidth);
      }
    },
    [isMobile, maxLeftPanelRatio, MIN_SIZE]
  );

  const handleMouseUp = useCallback(() => {
    isDragging.current = false;
    document.body.style.cursor = 'default';
  }, []);

  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  return (
    <Box
      className="arr-split-panel"
      ref={containerRef}
      data-testid="split-panel"
    >
      {/* Left Panel */}
      <Box
        className="left-panel"
        style={isMobile ? {height: leftHeight} : {width: leftWidth}}
        data-testid="left-panel"
        aria-label="Left Panel"
      >
        {leftPanel}
      </Box>

      {/* Divider */}
      <Box
        className="divider"
        onMouseDown={handleMouseDown}
        data-testid="divider"
        aria-label="Panel Divider"
      >
        <Box className="handle">
          <Box className="dot" />
          <Box className="dot" />
          <Box className="dot" />
        </Box>
      </Box>

      {/* Right Panel */}
      <Box
        className="right-panel"
        data-testid="right-panel"
        aria-label="Right Panel"
        style={
          !isMobile && fixedRightPanelWidth
            ? {width: `calc(100% - ${leftWidth}px - 12px - 13px)`}
            : {}
        }
      >
        {rightPanel}
      </Box>
    </Box>
  );
};

export default ArrSplitPanel;
